// 通用呼叫弹窗与通话逻辑
// 被调用文件：DeviceManagement.vue、DashboardView.vue 等
// 作用：提供统一的通话弹窗、WebRTC连接、音频处理等功能

import { ref } from "vue";
import { ElMessageBox, ElMessage } from "element-plus";
import jsCookie from "js-cookie";
import { logger } from "@/utils/logger";

// VERTC 类型声明（因为 externals 配置可能没有提供类型信息）
declare const VERTC: any;

// 获取设备显示名，优先级：device_name > deviceName > device_id > device_id
export function getDeviceDisplayName(device: any): string {
  return (
    device.device_name ||
    device.deviceName ||
    device.device_id ||
    device.device_id ||
    "Unknown Device"
  );
}

export function useCallDialog() {
  // 通话相关状态
  const currDeviceId = ref("");
  const currEventId = ref("");
  const joinRoomMsg = ref<any>(null);
  let currentEngine: any = null;
  let msgBox: any = null;

  const ALERT_VOLUME_STORAGE_KEY = "dashboard_alert_volume";
  const DEFAULT_ALERT_VOLUME = 0.6;

  const clampVolume = (value: number) => Math.min(Math.max(value, 0), 1);

  const getStoredAlertVolume = (): number => {
    if (typeof window === "undefined") {
      return DEFAULT_ALERT_VOLUME;
    }
    try {
      const stored = window.localStorage.getItem(ALERT_VOLUME_STORAGE_KEY);
      if (stored !== null) {
        const parsed = parseFloat(stored);
        if (!Number.isNaN(parsed)) {
          return clampVolume(parsed);
        }
      }
    } catch (error) {
      logger.debug("读取本地报警音量失败:", error);
    }
    return DEFAULT_ALERT_VOLUME;
  };

  // 添加引擎状态管理
  let isEngineInitializing = false;
  let isEngineInCall = false;
  let engineErrorCount = 0;
  const MAX_ERROR_COUNT = 3;

  // 检查 VERTC SDK 是否可用
  const checkVERTC = (): void => {
    if (typeof VERTC === "undefined" || !VERTC.createEngine) {
      throw new Error("VERTC SDK 未加载或不可用");
    }
  };

  // 处理呼叫响应 - 区分Web端发起和设备端发起的呼叫
  // 防止重复处理同一个消息
  let processingMessageId: string | null = null;
  let processingEventId: string | null = null;

  const handleCallResponse = async (
    message: any,
    device: any,
    isWebInitiated = false
  ) => {
    // 检查是否正在处理同一个消息
    if (processingMessageId === message.messageId) {
      logger.warn("正在处理同一个消息，跳过重复处理:", message.messageId);
      return;
    }

    // 检查是否正在处理同一个事件
    const eventId = message.data?.event_id;
    if (processingEventId === eventId && isEngineInCall) {
      logger.warn("正在处理同一个事件，跳过重复处理:", eventId);
      return;
    }

    logger.debug(
      "[handleCallResponse] message:",
      message,
      "device:",
      device,
      "isWebInitiated:",
      isWebInitiated
    );

    processingMessageId = message.messageId;
    processingEventId = eventId;
    joinRoomMsg.value = message;
    currEventId.value = eventId;
    currDeviceId.value = device.device_id || device.device_id;

    // 检查响应是否成功
    if (message.code === 0 || !message.code) {
      // 无论是Web端发起还是设备端发起的呼叫，都直接开始通话
      logger.info("开始通话");
      await startWebRTC(getDeviceDisplayName(device));
    } else {
      // 失败响应，显示错误消息
      ElMessage.error(`呼叫失败: ${message.message || "未知错误"}`);
    }

    // 处理完成后清除消息ID
    setTimeout(() => {
      processingMessageId = null;
      processingEventId = null;
    }, 5000);
  };

  // 开始WebRTC通话
  const startWebRTC = async (deviceName: string) => {
    logger.debug(
      "[startWebRTC] deviceName:",
      deviceName,
      "joinRoomMsg:",
      joinRoomMsg.value
    );

    // 检查是否已经在通话中
    if (isEngineInCall) {
      logger.warn("已在通话中，跳过重复启动");
      return;
    }

    // 检查是否正在初始化引擎
    if (isEngineInitializing) {
      logger.warn("引擎正在初始化中，跳过重复启动");
      return;
    }

    try {
      isEngineInitializing = true;
      await updateMessageBoxToAnswering(deviceName);
      logger.info("正在创建引擎...");

      // 检查VERTC SDK是否可用
      checkVERTC();

      // 如果已有引擎，先清理
      if (currentEngine) {
        logger.info("检测到已有引擎，先清理...");
        try {
          await currentEngine.leaveRoom();
          currentEngine = null;
        } catch (error) {
          logger.warn("清理旧引擎时出错:", error);
          currentEngine = null;
        }
        // 等待清理完成
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      // 重置错误计数
      engineErrorCount = 0;

      const appId = "675814fbeb91ca0172a00c82";
      // eslint-disable-next-line no-undef
      VERTC.setParameter("AUDIO_CODEC", "OPUS");
      // eslint-disable-next-line no-undef
      currentEngine = VERTC.createEngine(appId);
      logger.info("引擎创建成功");

      // 设置引擎事件监听
      currentEngine.on(
        "onUserPublishStream",
        (userId: string, mediaType: string) => {
          logger.info("收到远端用户发布流:", userId, mediaType);
        }
      );
      // 远端用户停止发布流
      currentEngine.on(
        "onUserUnpublishStream",
        (userId: string, mediaType: string) => {
          logger.info("远端用户停止发布流:", userId, mediaType);
        }
      );

      // 注册房间状态变化回调
      currentEngine.on(
        "onRoomStateChanged",
        (state: string, reason: string) => {
          logger.info("房间状态变化:", { state, reason });
          console.log("🔥 [VolcEngine RTC] 房间状态变化:", { state, reason });

          if (state === "disconnected" || state === "failed") {
            logger.warn("房间连接断开或失败，自动结束通话");
            console.log("🔥 [VolcEngine RTC] 房间连接断开，自动结束通话");
            // 自动结束通话
            leaveRoom();
          }
        }
      );

      // 注册用户加入房间回调（对齐文档事件名）
      currentEngine.on("onUserJoined", (userId: string) => {
        logger.info("用户加入房间:", userId);
        console.log("🔥 [VolcEngine RTC] 用户加入房间:", userId);
      });

      // 注册用户离开房间回调（对齐文档事件名）
      currentEngine.on("onUserLeave", (userId: string) => {
        logger.info("用户离开房间:", userId);
        console.log("🔥 [VolcEngine RTC] 用户离开房间:", userId);
      });

      // 注册连接状态变化回调
      currentEngine.on("onConnectionStateChanged", (state: string) => {
        logger.info("连接状态变化:", state);
        console.log("🔥 [VolcEngine RTC] 连接状态变化:", state);

        if (state === "disconnected" || state === "failed") {
          logger.warn("RTC连接断开或失败");
          console.log("🔥 [VolcEngine RTC] 连接断开，自动结束通话");
          // 连接断开时自动结束通话
          leaveRoom();
        }
      });

      // 注册网络质量回调
      currentEngine.on("onNetworkQuality", (quality: any) => {
        logger.debug("网络质量:", quality);
        console.log("🔥 [VolcEngine RTC] 网络质量:", quality);
      });

      // 注册音频设备状态回调
      currentEngine.on(
        "onAudioDeviceStateChanged",
        (deviceType: string, deviceState: string) => {
          logger.info("音频设备状态变化:", { deviceType, deviceState });
          console.log("🔥 [VolcEngine RTC] 音频设备状态变化:", {
            deviceType,
            deviceState,
          });
        }
      );

      currentEngine.on("onError", (err: Error) => {
        logger.error("引擎发生错误:", err);
        console.log("🔥 [VolcEngine RTC] 引擎发生错误:", err);
        engineErrorCount++;

        // 如果错误次数过多，清理引擎
        if (engineErrorCount >= MAX_ERROR_COUNT) {
          logger.error("引擎错误次数过多，清理引擎");
          console.log("🔥 [VolcEngine RTC] 引擎错误次数过多，清理引擎");
          if (currentEngine) {
            currentEngine = null;
          }
          isEngineInCall = false;
          isEngineInitializing = false;
          closeMessageBox();
        }
      });

      // 获取当前用户名
      const currentUser = jsCookie.get("username");
      if (!currentUser) {
        logger.error("未找到用户信息");
        return;
      }

      const roomConfig = {
        token: joinRoomMsg.value.data.token,
        room_id: joinRoomMsg.value.data.room_id,
        userId: currentUser,
      };

      logger.info("正在加入房间...");
      await currentEngine.joinRoom(
        roomConfig.token,
        roomConfig.room_id,
        {
          userId: roomConfig.userId,
        },
        {
          isAutoPublish: false,
          isAutoSubscribeAudio: true,
          isAutoSubscribeVideo: false,
        }
      );

      logger.info("成功加入房间");

      // 等待房间加入稳定
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // 检查引擎是否仍然有效
      if (!currentEngine) {
        throw new Error("引擎已失效，无法继续通话");
      }

      logger.info("正在开启音频采集...");
      await currentEngine.startAudioCapture();
      logger.info("音频采集已开启");

      // 再次检查引擎状态
      if (!currentEngine) {
        throw new Error("引擎已失效，无法发布音频流");
      }

      logger.info("正在发布音频流...");
      // eslint-disable-next-line no-undef
      await currentEngine.publishStream(VERTC.MediaType.AUDIO as any);

      logger.info("音频流发布成功，可以开始通话");

      // 设置通话状态
      isEngineInCall = true;
      isEngineInitializing = false;

      // 启动静默token刷新以确保紧急呼叫期间不中断
      const { startSilentTokenRefresh } = await import("../utils/auth");
      startSilentTokenRefresh();
    } catch (error) {
      logger.error("通话过程中发生错误:", error);
      // 发生错误时清理引擎
      if (currentEngine) {
        currentEngine = null;
      }
      isEngineInCall = false;
      isEngineInitializing = false;
      // 不要调用 leaveRoom，因为它会发送 end_call 消息
      // 直接关闭弹窗即可
      closeMessageBox();
    }
  };

  // 离开房间
  const leaveRoom = async () => {
    try {
      logger.info("正在离开房间...");
      const message = {
        type: "command",
        action: "end_call",
        messageId: `msg_${Date.now()}`,
        timestamp: new Date(),
        data: {
          event_id: currEventId.value,
          device_id: currDeviceId.value,
          source: "frontend",
        },
      };

      // 发送结束通话消息
      const websocketManager = await import(
        "../services/websocketManager"
      ).then((m) => m.default);
      websocketManager.send(message);

      joinRoomMsg.value = null;

      // 延迟刷新事件列表，确保后端处理完成
      setTimeout(async () => {
        try {
          // 动态导入 DataService
          const { DataService } = await import("../services/dataService");
          await DataService.getDashboardEventLists();
          logger.info("web端挂断后已刷新事件列表");
        } catch (error) {
          logger.warn("刷新事件列表失败:", error);
        }
      }, 1000);

      // 检查 currentEngine 是否存在
      if (currentEngine) {
        logger.info("正在停止音频...");
        try {
          // eslint-disable-next-line no-undef
          await currentEngine.unpublishStream(VERTC.MediaType.AUDIO as any);
          await currentEngine.stopAudioCapture();
        } catch (error) {
          logger.warn("停止音频时出错:", error);
        }

        logger.info("正在离开房间...");
        try {
          await currentEngine.leaveRoom();
        } catch (error) {
          logger.warn("离开房间时出错:", error);
        }
        currentEngine = null;
        logger.info("已离开房间");
      } else {
        logger.info("currentEngine 为 null，跳过清理步骤");
      }

      // 确保引擎被清理
      currentEngine = null;
      isEngineInCall = false;
      isEngineInitializing = false;
    } catch (error) {
      logger.error("离开房间时发生错误:", error);
    }
  };

  // 更新弹窗内容为通话中
  const updateMessageBoxToAnswering = (deviceName: string) => {
    logger.debug("[updateMessageBoxToAnswering] deviceName:", deviceName);
    msgBox = ElMessageBox.alert(
      `<div style="text-align: center;">
        <img src="/img/answering.gif" style="width: 300px; height: 100px; margin-bottom: 20px;" />
        <div style="font-size: 16px; color: #606266;">正在与设备 ${deviceName} 通话中...</div>
      </div>`,
      `设备：${deviceName}`,
      {
        confirmButtonText: "挂断",
        callback: () => {
          leaveRoom();
          closeMessageBox();
        },
        dangerouslyUseHTMLString: true,
        showClose: false,
        center: true,
      }
    );
  };

  // 关闭弹窗
  const closeMessageBox = () => {
    if (msgBox) {
      ElMessageBox.close();
    }
  };

  // 播放报警音效并显示接听弹窗（用于来电）
  const alertBroadcast = async (device_id: string, deviceName: string) => {
    logger.debug(
      "[alertBroadcast] device_id:",
      device_id,
      "deviceName:",
      deviceName
    );

    // 如果已有弹窗，先关闭
    if (msgBox) {
      closeMessageBox();
    }

    // 播放报警音效
    let alertSound = document.getElementById("alertSound") as HTMLAudioElement;
    if (!alertSound) {
      // 尝试其他可能的ID
      alertSound = document.getElementById(
        "deviceManagementAlertSound"
      ) as HTMLAudioElement;
    }
    if (!alertSound) {
      // 尝试查找任何音频元素
      const audioElements = document.querySelectorAll("audio");
      if (audioElements.length > 0) {
        alertSound = audioElements[0] as HTMLAudioElement;
      }
    }

    if (alertSound) {
      try {
        // 尝试设置音频源
        if (!alertSound.src) {
          alertSound.src = "/sound/alarm.mp3";
        }
        alertSound.loop = true;
        alertSound.volume = getStoredAlertVolume();
        alertSound.muted = false;
        alertSound.currentTime = 0;
        await alertSound.play();
        logger.debug("报警音效播放成功");
      } catch (e) {
        logger.debug("播放音频失败:", e);
        if (e instanceof Error && e.name === "NotAllowedError") {
          // 用户交互后才能播放音频，添加点击事件监听
          const playOnClick = () => {
            alertSound
              .play()
              .catch((err) => logger.debug("点击后播放音频失败:", err));
            document.removeEventListener("click", playOnClick);
          };
          document.addEventListener("click", playOnClick);
        }
      }
    } else {
      logger.warn("未找到报警音效元素");
    }

    // 显示接听弹窗
    msgBox = ElMessageBox.alert(
      `<div style="text-align: center;">
        <img src="/img/broadcast.gif" style="width: 100px; height: 100px; margin-bottom: 20px;" />
        <div style="margin-bottom: 10px;">
          <div style="font-size: 16px; color: #606266; margin-bottom: 5px;">设备编号：${device_id}</div>
          <div style="font-size: 16px; color: #606266;">设备名称：${deviceName}</div>
        </div>
      </div>`,
      "设备呼叫", // 弹窗标题
      {
        confirmButtonText: "接听",
        callback: () => {
          // 停止报警音效
          if (alertSound) {
            alertSound.pause();
            alertSound.currentTime = 0;
          } else {
            // 如果alertSound变量不可用，尝试直接查找音频元素
            const audioElements = document.querySelectorAll("audio");
            audioElements.forEach((audio) => {
              audio.pause();
              audio.currentTime = 0;
            });
          }
          // 开始通话
          startWebRTC(deviceName);
        },
        dangerouslyUseHTMLString: true,
        showClose: false,
        center: true,
      }
    );

    logger.debug("接听弹窗已显示");
  };

  return {
    // 状态
    currDeviceId,
    currEventId,
    joinRoomMsg,

    // 方法
    handleCallResponse,
    startWebRTC,
    leaveRoom,
    updateMessageBoxToAnswering,
    closeMessageBox,
    alertBroadcast,
  };
}
